import { createClient, type RealtimeChannel } from '@supabase/supabase-js';

export type IdVerificationDeliveryStatus =
  | 'requested'
  | 'sent'
  | 'delivered'
  | 'verified'
  | 'delivery_delayed'
  | 'failed'
  | 'bounced'
  | 'suppressed'
  | 'expired';

type VerificationStatusPayload = {
  status?: IdVerificationDeliveryStatus;
  sent_at?: string | null;
  delivered_at?: string | null;
  verified_at?: string | null;
  expires_at?: string | null;
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const publishableKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  '';

const statusTopicPattern = /^[A-Za-z0-9_-]{43}$/;
let realtimeClient: ReturnType<typeof createClient> | null = null;

const getRealtimeClient = () => {
  if (!supabaseUrl || !publishableKey) return null;
  realtimeClient ||= createClient(supabaseUrl, publishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'wersee-id-verification-realtime',
    },
  });
  return realtimeClient;
};

export const subscribeToIdVerificationStatus = (
  statusTopic: string,
  onStatus: (payload: VerificationStatusPayload) => void,
) => {
  if (!statusTopicPattern.test(statusTopic)) return () => undefined;
  const client = getRealtimeClient();
  if (!client) return () => undefined;

  let channel: RealtimeChannel | null = client
    .channel(`id-verification:${statusTopic}`)
    .on('broadcast', { event: 'status' }, ({ payload }) => {
      onStatus((payload || {}) as VerificationStatusPayload);
    })
    .subscribe();

  return () => {
    if (channel) void client.removeChannel(channel);
    channel = null;
  };
};
